/**
 * ImportCsvDialog — bulk-import bank items from a CSV file.
 *
 * Flow:
 *   1. Trainer picks a .csv file. PapaParse parses it client-side so we can
 *      preview the row count + headers BEFORE any server round-trip.
 *   2. Trainer clicks "Validate" → POST /import?dry_run=true. Backend returns
 *      either `{ would_create: N, errors: [...] }` (any errors block the
 *      import — all-or-nothing semantics) or `{ would_create: N, errors: [] }`.
 *   3. Trainer clicks "Import" → same endpoint without dry_run; rows are
 *      created as draft v1s in the bank.
 *
 * Why all-or-nothing? Partial inserts mean retry creates duplicates — much
 * worse trainer ergonomics than "fix your CSV, re-upload".
 *
 * The "Download template" link points at GET /question-banks/csv-template
 * which returns a starter CSV (one MCQ row + one True/False row).
 */
import { useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { useApi } from '../../hooks/useApi';
import {
  FileSpreadsheet, Upload, Download, AlertTriangle, CheckCircle2, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadAuthedFile } from '../../utils/downloadAuthedFile';

const MAX_BYTES = 2 * 1024 * 1024;
const REQUIRED_HEADERS = ['question_text', 'type', 'correct'];

export default function ImportCsvDialog({ open, onClose, bankId, onImported }) {
  const { post } = useApi();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null); // { rowCount, headers, missingHeaders }
  const [serverResult, setServerResult] = useState(null); // { created, would_create, errors[] }
  const [phase, setPhase] = useState('idle'); // idle | validating | importing
  const [parseError, setParseError] = useState(null);

  useEffect(() => {
    if (!open) {
      setFile(null); setParsed(null); setServerResult(null);
      setPhase('idle'); setParseError(null);
    }
  }, [open]);

  const pick = () => fileRef.current?.click();

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same filename after a failed parse
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast.error('Pick a .csv file.');
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error('CSV too large (max 2 MB). Split into multiple imports.');
      return;
    }
    setFile(f);
    setServerResult(null);
    setParseError(null);

    // Client-side parse for an early-feedback preview. PapaParse handles
    // BOM, quoted commas, multi-line cells, etc. — way more robust than
    // a hand-rolled split.
    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const headers = results.meta?.fields || [];
        const missingHeaders = REQUIRED_HEADERS.filter(h => !headers.includes(h));
        setParsed({
          rowCount: results.data.length,
          headers,
          missingHeaders,
        });
      },
      error: (err) => setParseError(err.message || 'CSV parse failed'),
    });
  };

  const runValidate = async () => {
    if (!file) return;
    setPhase('validating');
    setServerResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await post(
        `/question-banks/${bankId}/import?dry_run=true`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setServerResult(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Validation failed');
    } finally {
      setPhase('idle');
    }
  };

  const runImport = async () => {
    if (!file) return;
    setPhase('importing');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await post(
        `/question-banks/${bankId}/import`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (res.errors?.length) {
        // Backend rejected on row errors; show them
        setServerResult(res);
        toast.error(`Import blocked — ${res.errors.length} row(s) invalid.`);
        return;
      }
      toast.success(res.message || `Imported ${res.created} item(s)`);
      onImported?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setPhase('idle');
    }
  };

  // Authenticated template download — opening the URL in a new tab strips the
  // Authorization header (cookies are HttpOnly but cross-origin preview
  // deploys don't always pass them on top-level navigations), so we fetch
  // through the api client and trigger a Blob download instead.
  const handleDownloadTemplate = async () => {
    try {
      await downloadAuthedFile('/question-banks/csv-template', 'lumyn_bank_template.csv');
    } catch (e) {
      const status = e?.response?.status;
      toast.error(
        status === 401
          ? 'Your session has expired — please sign in again.'
          : status === 403
            ? 'Only editors can download the CSV template.'
            : 'Could not download the template. Try again in a moment.',
      );
    }
  };
  const busy = phase !== 'idle';
  const validationGreen = serverResult && serverResult.errors?.length === 0 && (serverResult.would_create ?? 0) > 0;
  const hasErrors = serverResult && (serverResult.errors?.length ?? 0) > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="import-csv-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[var(--lumyn-banks)]" />
            Import items from CSV
          </DialogTitle>
          <DialogDescription className="text-xs">
            Bulk-add bank items from a spreadsheet. All rows must validate — if any row has a problem,
            nothing is imported. Imported items land as <span className="font-medium">draft</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {/* Template link — uses authenticated fetch+Blob so cross-origin
              preview deploys don't 404/401 on the new-tab navigation. */}
          <button
            type="button"
            onClick={handleDownloadTemplate}
            data-testid="csv-template-link"
            className="inline-flex items-center gap-1.5 text-xs text-[var(--lumyn-banks)] hover:underline bg-transparent border-0 p-0 cursor-pointer"
          >
            <Download className="w-3.5 h-3.5" />Download CSV template (MCQ, True/False & Free text examples)
          </button>

          {/* Drop zone */}
          <div
            onClick={pick}
            className="border-2 border-dashed border-[var(--lumyn-banks)]/30 rounded-lg p-6 text-center cursor-pointer hover:bg-[var(--lumyn-banks)]/5 transition-colors"
            data-testid="csv-drop-zone"
          >
            <FileSpreadsheet className="w-7 h-7 mx-auto text-[var(--lumyn-banks)]/60 mb-2" />
            {file ? (
              <>
                <p className="text-sm font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium">Click to choose a CSV file</p>
                <p className="text-xs text-muted-foreground">Required columns: question_text, type, correct &middot; up to 10 options &middot; multiple correct answers: correct=1;3;4 &middot; max 2 MB</p>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              className="hidden"
              data-testid="csv-file-input"
            />
          </div>

          {/* Client-side parse summary */}
          {parseError && (
            <div className="text-xs text-red-600 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />{parseError}
            </div>
          )}
          {parsed && !parseError && (
            <div className="rounded-md border bg-card p-3 text-xs space-y-1.5" data-testid="csv-preview">
              <div className="flex items-center justify-between">
                <span><span className="font-medium">{parsed.rowCount}</span> data row{parsed.rowCount === 1 ? '' : 's'} detected</span>
                <span className="text-muted-foreground">{parsed.headers.length} column{parsed.headers.length === 1 ? '' : 's'}</span>
              </div>
              {parsed.missingHeaders.length > 0 ? (
                <div className="text-amber-700 dark:text-amber-400 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>Missing required column(s): <span className="font-mono">{parsed.missingHeaders.join(', ')}</span>. Re-export with the template headers.</span>
                </div>
              ) : (
                <div className="text-muted-foreground">Required headers present. Click <span className="font-medium">Validate</span> for full row-by-row check.</div>
              )}
            </div>
          )}

          {/* Server-side result */}
          {hasErrors && (
            <div className="rounded-md border border-red-200 bg-red-50/60 dark:bg-red-950/30 dark:border-red-900/60 p-3 text-xs space-y-1.5 max-h-48 overflow-y-auto" data-testid="csv-errors">
              <div className="flex items-center gap-1.5 font-medium text-red-700 dark:text-red-300">
                <AlertTriangle className="w-3.5 h-3.5" />
                {serverResult.errors.length} row(s) invalid — fix and re-upload
              </div>
              <ul className="space-y-0.5 pl-5 list-disc">
                {serverResult.errors.slice(0, 20).map((e, i) => (
                  <li key={`err-${i}`}><span className="font-mono">row {e.row}</span> — {e.message}</li>
                ))}
                {serverResult.errors.length > 20 && (
                  <li className="text-muted-foreground">…and {serverResult.errors.length - 20} more.</li>
                )}
              </ul>
            </div>
          )}
          {validationGreen && (
            <div className="rounded-md border border-green-200 bg-green-50/60 dark:bg-green-950/30 dark:border-green-900/60 p-3 text-xs flex items-center gap-1.5 text-green-700 dark:text-green-300" data-testid="csv-validation-ok">
              <CheckCircle2 className="w-4 h-4" />
              All {serverResult.would_create} row(s) valid. Click <span className="font-medium">Import</span> to create them as drafts.
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant="outline"
            onClick={runValidate}
            disabled={busy || !file || (parsed?.missingHeaders?.length ?? 0) > 0}
            data-testid="csv-validate-btn"
          >
            {phase === 'validating'
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
            Validate
          </Button>
          <Button
            onClick={runImport}
            disabled={busy || !file || (parsed?.missingHeaders?.length ?? 0) > 0}
            data-testid="csv-import-btn"
            className="bg-[var(--lumyn-banks)] hover:bg-[var(--lumyn-banks)]/90 text-white"
          >
            {phase === 'importing'
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <Upload className="w-4 h-4 mr-1.5" />}
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
